import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from '../../../../ui/toast';
import { getEmployeeList } from '../../../../utils/api_service/employees';
import { useLazyGetJobOpeningListQuery } from '../../../../utils/api_service/jobOpeningsApi';
import useDebouncedEmployeeSearch from '../../../../utils/helpers/AsyncSelect/EmployeeGet';
import { EmployeeUser } from '../../../../utils/types/admin_types';
import {
  JobOpeningList,
  JobOpeningRequest,
  sortJobOpening,
} from '../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import {
  DEFAULT_DRAFT_FILTERS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
  JOB_OPENINGS_TAB_VALUES,
} from '../constants';
import {
  IJobOpeningsAppliedFilters,
  IJobOpeningsDraftFilters,
  IUseJobOpeningsReturn,
} from '../interfaces';

export const useJobOpenings = (): IUseJobOpeningsReturn => {
  const history = useHistory();

  // RTK Query lazy hook
  const [triggerGetJobOpenings, { isFetching: isQueryFetching }] =
    useLazyGetJobOpeningListQuery();

  const [jobOpeningData, setJobOpeningData] = useState<JobOpeningList[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState<boolean>(false);

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<string>(JOB_OPENINGS_TAB_VALUES.ACTIVE);
  const [sort, setSort] = useState<sortJobOpening>(DEFAULT_SORT);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [isFiltersApplied, setIsFiltersApplied] = useState<boolean>(false);

  // Search input state (search by title)
  const [searchJobTitle, setSearchJobTitle] = useState<string>('');

  // Draft filters (for drawer)
  const [draftFilters, setDraftFilters] = useState<IJobOpeningsDraftFilters>(DEFAULT_DRAFT_FILTERS);

  // Applied filters (for actual queries)
  const [appliedFiltersState, setAppliedFiltersState] =
    useState<IJobOpeningsDraftFilters>(DEFAULT_DRAFT_FILTERS);

  // Employees for select dropdowns
  const [defaultEmployeeUserList, setDefaultEmployeeUserList] = useState<EmployeeUser[]>([]);
  const { loadOptionsDebounced } = useDebouncedEmployeeSearch();

  const tabs = useMemo(
    () => [{ label: JOB_OPENINGS_TAB_VALUES.ACTIVE }, { label: JOB_OPENINGS_TAB_VALUES.CLOSED }],
    [],
  );

  // Load initial employee list for dropdown suggestions
  useEffect(() => {
    let isMounted = true;
    getEmployeeList('', 1, 10)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Failed to fetch employees');
      })
      .then((data) => {
        if (isMounted && data?.employee) {
          setDefaultEmployeeUserList(data.employee);
        }
      })
      .catch(() => {
        // Soft error for optional employee list
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const defaultEmployeeUserOptions = useMemo<SelectOptions[]>(
    () =>
      defaultEmployeeUserList.map((employee) => ({
        value: employee.id,
        label: `${employee.user.firstName} ${employee.user.lastName}`,
      })),
    [defaultEmployeeUserList],
  );

  // Compute display applied filters for chips
  const appliedFilters: IJobOpeningsAppliedFilters = useMemo(() => {
    const filters: IJobOpeningsAppliedFilters = {};
    if (appliedFiltersState.jobTitle) {
      filters['Job Title'] = [appliedFiltersState.jobTitle];
    }
    if (appliedFiltersState.jobReferenceNumber) {
      filters['Job Reference'] = [appliedFiltersState.jobReferenceNumber];
    }
    if (appliedFiltersState.hiringManager?.label) {
      filters['Hiring Manager'] = [appliedFiltersState.hiringManager.label];
    }
    if (appliedFiltersState.internalRecruiter?.label) {
      filters['Internal Recruiter'] = [appliedFiltersState.internalRecruiter.label];
    }
    if (appliedFiltersState.tags && appliedFiltersState.tags.length > 0) {
      filters['Tags'] = appliedFiltersState.tags;
    }
    return filters;
  }, [appliedFiltersState]);

  // Main fetch function using RTK Query
  const fetchJobOpenings = async (page: number) => {
    setLoading(true);
    const requestBody: JobOpeningRequest = {
      jobTitle: appliedFiltersState.jobTitle,
      jobReferenceNumber: appliedFiltersState.jobReferenceNumber,
      hiringManager: appliedFiltersState.hiringManager ? appliedFiltersState.hiringManager.value : '',
      internalRecruiter: appliedFiltersState.internalRecruiter
        ? appliedFiltersState.internalRecruiter.value
        : '',
      tags: appliedFiltersState.tags.length > 0 ? appliedFiltersState.tags : undefined,
      sort,
      active: activeTab === JOB_OPENINGS_TAB_VALUES.ACTIVE,
    };

    try {
      const result = await triggerGetJobOpenings({
        body: requestBody,
        page,
        limit: DEFAULT_PAGE_SIZE,
      }).unwrap();

      setJobOpeningData(result.jobOpenings || []);
      setTotalRecords(result.totalJobOpenings || 0);
      setTotalPages(Math.max(1, Math.ceil((result.totalJobOpenings || 0) / DEFAULT_PAGE_SIZE)));
      setHasFetchedOnce(true);
    } catch (error) {
      toast.error('Error Getting Job Openings', { position: 'bottom-center' });
      setJobOpeningData([]);
      setTotalRecords(0);
      setTotalPages(1);
      setHasFetchedOnce(true);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search / filter trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchJobOpenings(pageNumber);
    }, 400);

    return () => clearTimeout(timer);
  }, [appliedFiltersState, sort, activeTab, pageNumber]);

  // Filter handlers
  const handleOpenFilter = () => {
    setDraftFilters({ ...appliedFiltersState });
    setFilterOpen(true);
  };

  const handleCloseFilter = () => {
    setFilterOpen(false);
  };

  const handleDraftFilterChange = <K extends keyof IJobOpeningsDraftFilters>(
    key: K,
    value: IJobOpeningsDraftFilters[K],
  ) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleManagerChange = (opt: SelectOptions, isHiring: boolean) => {
    if (isHiring) {
      handleDraftFilterChange('hiringManager', opt);
    } else {
      handleDraftFilterChange('internalRecruiter', opt);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFiltersState({ ...draftFilters });
    setIsFiltersApplied(true);
    setPageNumber(1);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setDraftFilters(DEFAULT_DRAFT_FILTERS);
    setAppliedFiltersState(DEFAULT_DRAFT_FILTERS);
    setSearchJobTitle('');
    setIsFiltersApplied(false);
    setPageNumber(1);
    setFilterOpen(false);
  };

  const handleRemoveFilter = (label: string, value: string | null) => {
    setPageNumber(1);
    setAppliedFiltersState((prev) => {
      const next = { ...prev };
      switch (label) {
        case 'Job Title':
          next.jobTitle = '';
          setSearchJobTitle('');
          break;
        case 'Job Reference':
          next.jobReferenceNumber = '';
          break;
        case 'Hiring Manager':
          next.hiringManager = undefined;
          break;
        case 'Internal Recruiter':
          next.internalRecruiter = undefined;
          break;
        case 'Tags':
          next.tags = next.tags.filter((t) => t !== value);
          break;
        default:
          break;
      }
      return next;
    });

    setDraftFilters((prev) => {
      const next = { ...prev };
      switch (label) {
        case 'Job Title':
          next.jobTitle = '';
          break;
        case 'Job Reference':
          next.jobReferenceNumber = '';
          break;
        case 'Hiring Manager':
          next.hiringManager = undefined;
          break;
        case 'Internal Recruiter':
          next.internalRecruiter = undefined;
          break;
        case 'Tags':
          next.tags = next.tags.filter((t) => t !== value);
          break;
        default:
          break;
      }
      return next;
    });
  };

  const isFilterDisabled = (filterKey: string): boolean => {
    return isFiltersApplied && !!appliedFilters[filterKey] && appliedFilters[filterKey].length > 0;
  };

  // Search input handler
  const handleSearchChange = (value: string) => {
    setSearchJobTitle(value);
    setPageNumber(1);
    setAppliedFiltersState((prev) => ({ ...prev, jobTitle: value }));
    setDraftFilters((prev) => ({ ...prev, jobTitle: value }));
  };

  // Sort handler
  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSort((prevSort) => {
      const newSort: sortJobOpening = { ...prevSort };
      if (newSort[key as keyof sortJobOpening] === direction) {
        delete newSort[key as keyof sortJobOpening];
      } else {
        newSort[key as keyof sortJobOpening] = direction;
      }
      return newSort;
    });
  };

  const onPageChange = (page: number) => {
    setPageNumber(page);
  };

  const onRowClick = (id: string) => {
    history.push(`/job-openings/${id}`);
  };

  return {
    loading: loading || isQueryFetching,
    isFetching: isQueryFetching,
    jobOpeningData,
    totalRecords,
    totalPages,
    pageNumber,
    onPageChange,
    activeTab,
    setActiveTab: (tab: string) => {
      setActiveTab(tab);
      setSearchJobTitle('');
      setPageNumber(1);
    },
    tabs,
    sort,
    handleSortChange,
    searchJobTitle,
    handleSearchChange,
    filterOpen,
    handleOpenFilter,
    handleCloseFilter,
    draftFilters,
    handleDraftFilterChange,
    handleManagerChange,
    handleApplyFilters,
    handleClearFilters,
    handleRemoveFilter,
    appliedFilters,
    isFiltersApplied,
    isFilterDisabled,
    defaultEmployeeUserOptions,
    loadOptionsDebounced,
    onRowClick,
    hasFetchedOnce,
  };
};
